import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';

/**
 * Lightbox for the current page's mockup. When the page has an assigned mockup it
 * shows the image; when it doesn't, it shows a picker to choose one from the round's
 * pool (`availableMockups`). Presentational — the host supplies the data and handles
 * close + selection.
 */
@Component({
  selector: 'app-mockup-viewer',
  templateUrl: './mockup-viewer.html',
  styleUrls: ['./mockup-viewer.scss', './mockup-viewer.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MockupViewer {
  /** Whether the lightbox is shown. */
  readonly open = input(false);

  /** The assigned mockup image URL (null when the page has none assigned). */
  readonly mockupUrl = input<string | null>(null);

  /** The page label shown in the subtitle (e.g. the path). */
  readonly pageLabel = input('');

  /** All mockups in the round — the pool shown when no mockup is assigned. */
  readonly availableMockups = input<string[]>([]);

  /** Whether an assignment request is in flight. */
  readonly assigning = input(false);

  /** Emits when the lightbox should close. */
  readonly close = output<void>();

  /** Emits the chosen mockup URL to assign to the current page. */
  readonly mockupSelect = output<string>();

  /** The mockup highlighted in the picker before confirming. */
  readonly chosen = signal<string | null>(null);

  /** Derives a short, readable filename from a mockup URL for the picker labels. */
  mockupLabel(mockupUrl: string): string {
    try {
      const path = new URL(mockupUrl).pathname;
      const name = decodeURIComponent(path.split('/').pop() ?? '');
      return name || mockupUrl;
    } catch {
      return mockupUrl.split('/').pop() || mockupUrl;
    }
  }

  /** Tracks the dropdown selection so the preview + Assign button can react. */
  onPick(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.chosen.set(value || null);
  }

  /** Confirms the highlighted mockup, emitting it for the host to assign. */
  confirmSelection(): void {
    const selected = this.chosen();
    if (selected) {
      this.mockupSelect.emit(selected);
    }
  }
}
